import {Dictionary} from "../../page/elements/dictionary";
import {LanguageProfile, LanguagesService} from "../../common/languages";
import {resolve} from "../../container";
import {ManagingResources} from "../../common/lifetime";
import {Cookies} from "../../common/cookies";
import {DeviceDetectorService} from "../../common/device";
import Highcharts from "highcharts";
import "highcharts/modules/data";
import "highcharts/modules/exporting";
import "highcharts/modules/offline-exporting";
import "highcharts/modules/export-data";
import "highcharts/modules/accessibility";
import "highcharts/modules/treemap";
import "highcharts/modules/variable-pie";
import "highcharts/modules/full-screen";
import "highcharts/highcharts-more";
import type {Maybe} from "../../bootstrap/common/types";

const CHART_FULLSCREEN_SPACING = 24;
const CONTEXT_BUTTON_SPACING = 36;

export class EopHighcharts extends ManagingResources(HTMLElement) {
    private activeLanguage: LanguageProfile;
    protected chart: Highcharts.Chart;

    public constructor(private device: DeviceDetectorService = resolve(DeviceDetectorService), protected cookies: Cookies = resolve(Cookies)) {
        super();
        const languageService: LanguagesService = resolve(LanguagesService);
        this.activeLanguage = languageService.activeLanguage();
    }

    public connectedCallback(): void {
        const dictionary = Dictionary.of(this);
        const caption = this.querySelector(".chart-caption-template")?.innerHTML;
        const defaultOptions = this.initHighchartsConfig(dictionary, caption);
        const cmsDefaultOptions = this.readRawData<Highcharts.Options>("highcharts-default-config");
        const cmsOptions = this.readRawData<Highcharts.Options>("highcharts-config");
        const finalOptions = Highcharts.merge(defaultOptions, cmsDefaultOptions, cmsOptions);

        this.chart = Highcharts.chart(this, finalOptions);
    }

    public toggleDialog(): void {
        this.chart.fullscreen.toggle();
    }

    private initHighchartsConfig(dictionary: Dictionary, caption: Maybe<string>): Highcharts.Options {
        this.addFullscreenIconToRenderer();
        let highchartsOptions: Highcharts.Options = {
            chart: {
                events: {
                    fullscreenOpen: function (this: Highcharts.Chart, _event: any): void {
                        this.container.classList.add("fullscreen");
                        this.update({
                            chart: {
                                spacingLeft: CHART_FULLSCREEN_SPACING,
                                spacingTop: CHART_FULLSCREEN_SPACING + CONTEXT_BUTTON_SPACING,
                                spacingRight: CHART_FULLSCREEN_SPACING,
                                spacingBottom: CHART_FULLSCREEN_SPACING
                            }
                        });
                    },
                    fullscreenClose: function (this: Highcharts.Chart, _event: any): void {
                        this.container.classList.remove("fullscreen");
                        this.update({chart: {spacingLeft: 4, spacingTop: CONTEXT_BUTTON_SPACING, spacingRight: 0, spacingBottom: 0}});
                    }
                }
            },
            lang: {
                viewFullscreen: dictionary.translate("MSG_CHARTS_FULLSCREEN"),
                contextButtonTitle: dictionary.translate("MSG_CHARTS_CONTEXT_BUTTON"),
                printChart: dictionary.translate("MSG_CHARTS_PRINT"),
                downloadPNG: dictionary.translate("MSG_CHARTS_DOWNLOAD_AS_PNG"),
                downloadJPEG: dictionary.translate("MSG_CHARTS_DOWNLOAD_AS_JPEG"),
                downloadPDF: dictionary.translate("MSG_CHARTS_DOWNLOAD_AS_PDF"),
                downloadCSV: dictionary.translate("MSG_CHARTS_DOWNLOAD_AS_CSV"),
                decimalPoint: this.activeLanguage.numberDecimalSeparator,
                thousandsSep: this.activeLanguage.numberGroupSeparator
            },
            plotOptions: {
                series: {
                    animation: this.cookies.get("eop-disable-animations") !== "true"
                }
            },
            caption: {
                useHTML: true,
                text: caption
            }
        };

        // iOS does not support fullscreen mode
        if (!this.device.isIOS()) {
            const fullscreenButtonToggle = {
                exporting: {
                    buttons: {
                        fullscreenButton: {
                            symbol: "fullscreen",
                            titleKey: "viewFullscreen",
                            className: "highcharts-contextbutton",
                            buttonSpacing: 0,
                            y: -CONTEXT_BUTTON_SPACING,
                            onclick: (event: Event) => {
                                const target = event.target as HTMLElement;
                                const highchartsContainer: EopHighcharts = target.closest(".enbw-highcharts")!;
                                highchartsContainer.toggleDialog();
                            }
                        }
                    }
                }
            };
            highchartsOptions = Highcharts.merge(fullscreenButtonToggle, highchartsOptions);
        }
        return highchartsOptions;
    }

    private addFullscreenIconToRenderer(): void {
        // need to use vector icon, since using images does not work with client-side chart exporting
        Highcharts.SVGRenderer.prototype.symbols.fullscreen = (x: number, y: number, w: number, h: number) => [
            // Top left bracket
            "M", x + w * 0.1, y + h * 0.2,
            "L", x + w * 0.1, y + h * 0.5,
            "M", x + w * 0.1, y + h * 0.2,
            "L", x + w * 0.4, y + h * 0.2,
            // Bottom right bracket
            "M", x + w * 0.9, y + h * 0.9,
            "L", x + w * 0.9, y + (h * 0.6),
            "M", x + w * 0.9, y + h * 0.9,
            "L", x + w * 0.6, y + h * 0.9
        ];
    }
}

customElements.define("eop-highcharts", EopHighcharts);